import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import {
  Alert,
  Badge,
  Card,
  CardBody,
  Col,
  Container,
  Label,
  Row,
  Spinner,
  Table,
} from 'reactstrap'
import Breadcrumbs from '../../../components/Common/Breadcrumb'
import Select from '../../../components/Template/Input/Select'
import { getRegs } from '../../../utils/fc'
import { fetchExtratoRhHistorico } from '../Dashboard/rhDashboardApi'
import {
  canSelectCollaborator,
  userDisplayName,
  userGraphId,
} from './access'
import { fmtOrDash, MonthHoursCell } from './flags'
import MonthAccordionPanel from './MonthAccordion'

const ActivitiesReport = ({ auth }) => {
  const user = auth?.user
  const canSelect = canSelectCollaborator(user)

  const selfOption = useMemo(() => {
    if (!user) return null
    return {
      value: userGraphId(user),
      label: userDisplayName(user),
      pf_id: user.pf_id,
      pf_graphId: userGraphId(user),
    }
  }, [user])

  const [selectedUser, setSelectedUser] = useState(selfOption)
  const [colaboradores, setColaboradores] = useState([])
  const [loadingColaboradores, setLoadingColaboradores] = useState(false)
  const [historico, setHistorico] = useState(null)
  const [loadingHistorico, setLoadingHistorico] = useState(false)
  const [historicoError, setHistoricoError] = useState(null)
  const [expandedMes, setExpandedMes] = useState(null)

  useEffect(() => {
    setSelectedUser(selfOption)
  }, [selfOption])

  const loadHistorico = useCallback(async (graphId) => {
    if (!graphId) return
    setLoadingHistorico(true)
    setHistoricoError(null)
    setExpandedMes(null)
    try {
      const data = await fetchExtratoRhHistorico(graphId)
      if (data?.ok === false) {
        setHistorico(null)
        setHistoricoError(data.msg || 'Erro ao carregar histórico de meses.')
      } else {
        setHistorico(data)
      }
    } catch (err) {
      setHistorico(null)
      setHistoricoError(String(err?.message || err || 'Erro ao carregar histórico de meses.'))
    } finally {
      setLoadingHistorico(false)
    }
  }, [])

  useEffect(() => {
    const graphId = selectedUser?.pf_graphId || selectedUser?.value
    if (graphId) loadHistorico(graphId)
  }, [selectedUser, loadHistorico])

  useEffect(() => {
    if (!canSelect) return undefined
    let cancelled = false
    const load = async () => {
      setLoadingColaboradores(true)
      try {
        const data = await getRegs('pf', { pf_ativo: 1 })
        const idsComContratoAtivo = new Set()
        try {
          const contratos = await getRegs('contratoForn', {
            contratoForn_ativo: 1,
            contratoForn_parteTab: 'pf',
          })
          if (Array.isArray(contratos)) {
            contratos.forEach((c) => {
              if (c.contratoForn_parteId) {
                idsComContratoAtivo.add(String(c.contratoForn_parteId))
              }
            })
          }
        } catch (err) {
          console.error('Erro ao buscar contratos', err)
        }

        if (cancelled) return
        if (Array.isArray(data)) {
          const filtered = data
            .filter(u => u.pf_nomeExibicao && u.pf_graphId)
            .filter(u => idsComContratoAtivo.size === 0 || idsComContratoAtivo.has(String(u.pf_id)))
            .map(u => ({
              value: u.pf_graphId,
              label: u.pf_nomeExibicao,
              pf_id: u.pf_id,
              pf_graphId: u.pf_graphId,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
          setColaboradores(filtered)
        }
      } catch (err) {
        console.error('Erro ao buscar colaboradores', err)
      } finally {
        if (!cancelled) setLoadingColaboradores(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [canSelect])

  const meses = useMemo(() => {
    const list = Array.isArray(historico?.meses) ? [...historico.meses] : []
    return list.sort((a, b) => String(b.mes_ref || '').localeCompare(String(a.mes_ref || '')))
  }, [historico])

  const graphId = selectedUser?.pf_graphId || selectedUser?.value || userGraphId(user)
  const collaboratorName = selectedUser?.label || userDisplayName(user)

  const cargaHorariaFmt = (row) => (
    row.carga_contratada_fmt
    || row.carga_contratada_vigente_fmt
    || '—'
  )

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col>
            <Breadcrumbs title="Relatório de atividades" breadcrumbItems={[]} />
          </Col>
        </Row>

        <Row className="mb-3">
          <Col>
            <h4 className="text-muted mb-0">
              Colaborador:
              {' '}
              {collaboratorName}
            </h4>
          </Col>
        </Row>

        <Row style={{ marginBottom: 24 }}>
          <Col xl={4} lg={5} md={6} sm={12}>
            <Label>Colaborador</Label>
            <Select
              isClearable={false}
              isSearchable={canSelect}
              isDisabled={!canSelect}
              isLoading={loadingColaboradores}
              placeholder={canSelect ? 'Selecione o colaborador...' : collaboratorName}
              value={selectedUser}
              options={canSelect ? colaboradores : (selfOption ? [selfOption] : [])}
              onChange={(opt) => {
                if (!canSelect) return
                setSelectedUser(opt)
              }}
            />
            {!canSelect && (
              <small className="text-muted">
                Exibindo somente seus dados. Seletor disponível para administradores (perm_id = 1) e Ronaldo.
              </small>
            )}
          </Col>
        </Row>

        {loadingHistorico && (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        )}

        {!loadingHistorico && historicoError && (
          <Alert color="danger">{historicoError}</Alert>
        )}

        {!loadingHistorico && !historicoError && meses.length === 0 && (
          <Card>
            <CardBody>
              <p className="text-center mb-0 p-2">
                Não foram encontrados meses de contrato / atividades para este colaborador.
              </p>
            </CardBody>
          </Card>
        )}

        {!loadingHistorico && !historicoError && meses.length > 0 && (
          <Card className="mb-4">
            <div className="table-responsive">
              <Table striped bordered hover className="mb-0" style={{ fontSize: '12px' }}>
                <thead>
                  <tr>
                    <th>Mês</th>
                    <th>Carga horária</th>
                    <th>Fechamento de horas</th>
                    <th>Horas lançadas</th>
                    <th>Variação mensal</th>
                    <th>Saldo acumulado</th>
                    <th>Feriados</th>
                    <th>Abono</th>
                  </tr>
                </thead>
                <tbody>
                  {meses.map((row) => {
                    const isOpen = expandedMes === row.mes
                    return (
                      <React.Fragment key={row.mes_ref || row.mes}>
                        <tr
                          role="button"
                          tabIndex={0}
                          style={{ cursor: 'pointer', background: isOpen ? '#eef2ff' : undefined }}
                          onClick={() => setExpandedMes(isOpen ? null : row.mes)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              setExpandedMes(isOpen ? null : row.mes)
                            }
                          }}
                        >
                          <td>
                            <i className={`mdi ${isOpen ? 'mdi-chevron-down' : 'mdi-chevron-right'} mr-1`} />
                            <strong>{row.mes}</strong>
                            {row.fechamento_ativo ? (
                              <Badge color="success" pill className="ml-2" style={{ fontSize: '0.65rem' }}>
                                Fechado
                              </Badge>
                            ) : (
                              <Badge color="secondary" pill className="ml-2" style={{ fontSize: '0.65rem' }}>
                                Aberto
                              </Badge>
                            )}
                          </td>
                          <td>{cargaHorariaFmt(row)}</td>
                          <td>{fmtOrDash(row.carga_realizada_fmt)}</td>
                          <td>{fmtOrDash(row.carga_realizada_live_fmt)}</td>
                          <td>{fmtOrDash(row.saldo_mes_fmt)}</td>
                          <td>{fmtOrDash(row.banco_acumulado_fmt)}</td>
                          <td
                            style={Number(row.feriado_s) > 0 ? { background: '#d1ecf1' } : undefined}
                            title={Number(row.feriado_s) > 0 ? 'Há feriado lançado neste mês' : undefined}
                          >
                            <MonthHoursCell
                              value={row.feriado_fmt}
                              seconds={row.feriado_s}
                              kind="feriado"
                            />
                          </td>
                          <td
                            style={Number(row.ferias_s) > 0 ? { background: '#e8eaf6' } : undefined}
                            title={
                              Number(row.ferias_s) > 0
                                ? `Inclui férias: ${row.ferias_fmt || ''}`.trim()
                                : undefined
                            }
                          >
                            <MonthHoursCell
                              value={row.abono_fmt}
                              seconds={row.abono_s}
                              feriasSeconds={row.ferias_s}
                              kind="abono"
                            />
                          </td>
                        </tr>
                        {isOpen && (
                          <MonthAccordionPanel
                            mes={row.mes}
                            graphId={graphId}
                            isOpen={isOpen}
                          />
                        )}
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </Table>
            </div>
            {historico?.resumo && (
              <CardBody className="py-2 border-top small text-muted d-flex flex-wrap" style={{ gap: 24 }}>
                <span>
                  Carga contratada (fechamentos):
                  {' '}
                  <strong>{historico.resumo.carga_contratada_fmt || '—'}</strong>
                </span>
                <span>
                  Carga realizada (fechamentos):
                  {' '}
                  <strong>{historico.resumo.carga_realizada_fmt || '—'}</strong>
                </span>
                <span>
                  Banco acumulado atual:
                  {' '}
                  <strong>{historico.resumo.banco_acumulado_fmt || '—'}</strong>
                </span>
              </CardBody>
            )}
          </Card>
        )}
      </Container>
    </div>
  )
}

const mapStateToProps = state => ({
  auth: state.auth,
})

export default connect(mapStateToProps)(ActivitiesReport)
